import { useEffect, useRef } from 'react';
import { apiFetch } from '@/lib/apiClient';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DiscoveryJobStatus {
  status: 'pending' | 'running' | 'done' | 'failed' | 'deferred';
  emailCount?: number;
  /** Oldest scanned item (ISO) — how far back the sample reaches. */
  oldestDate?: string;
  /** Calendar scan: actual span covered by the scanned events (ISO). */
  oldestEventDate?: string;
  furthestEventDate?: string;
  fetchFailed?: boolean;
  professionInference?: string;
  inboxCategories?: string[];
  entities?: string[];
  contactCount?: number;
  subjectKeywords?: string[];
  sentTopics?: Array<{ topic: string; count: number }>;
  isEmpty?: boolean;
  isSparse?: boolean;
  upcomingCount?: number;
  pastCount?: number;
  protectedTimeLabels?: string[];
  projects?: string[];
  recurringMeetings?: Array<{ title: string; count: number }>;
  travelTrips?: Array<{ destination: string; dateRange: string; city?: string; lodging?: string; startISO?: string; endISO?: string }>;
  /** LLM read of the class of hotels the trips surfaced. */
  lodgingRead?: string;
  restaurantReservations?: Array<{ name: string; city?: string; whenISO: string; date: string }>;
  keyAppointments?: Array<{ name: string; city?: string; lastISO: string; lastDate: string; count: number }>;
  revealText?: string;
  contactsSummary?: string;
  contactsCount?: number;
  pageId?: string;
}

interface DiscoveryStatus {
  jobs: {
    inbox: DiscoveryJobStatus;
    sent: DiscoveryJobStatus;
    calendar: DiscoveryJobStatus;
    letter: DiscoveryJobStatus;
  };
  allDone: boolean;
}

// ─── Timing constants ────────────────────────────────────────────────────────

const POLL_MS          = 1_500;   // how often to check job status
const MIN_PHASE_MS     = 4_000;   // minimum time between status and result per phase
const INTER_PHASE_MS   = 1_200;   // pause between result of one phase and lead-in of next
const HARD_TIMEOUT_MS  = 90_000;  // bail if jobs never finish

// ─── Status (lead-in) messages per phase ─────────────────────────────────────

const PHASE_STATUS: Record<string, string> = {
  inbox:    `Reading the shape of your inbox...`,
  sent:     `Looking at what you actually send...`,
  calendar: `Checking how your time is spent...`,
  letter:   `Putting it together...`,
};

// Extra "still working" messages shown if a phase takes longer than MIN_PHASE_MS
const PHASE_PATIENCE: Record<string, string> = {
  inbox:    `Sorting the signal from the noise...`,
  sent:     `Tracing the throughline...`,
  calendar: `Reading the rhythm of your weeks...`,
  letter:   `Almost there...`,
};

// ─── Result-message builders ──────────────────────────────────────────────────

// Humanize category buckets into a readable list ("banking, code repos, and infra")
function joinCategories(cats: string[]): string {
  const c = cats.slice(0, 4);
  if (c.length === 0) return '';
  if (c.length === 1) return c[0];
  if (c.length === 2) return `${c[0]} and ${c[1]}`;
  return `${c.slice(0, -1).join(', ')}, and ${c[c.length - 1]}`;
}

// [inbox] — reframe raw services into an identity. Name actual entities when we have them.
function buildInboxMessage(job: DiscoveryJobStatus): string {
  if (job.fetchFailed) {
    return `I had trouble reading your inbox — you may need to reconnect your Google account.`;
  }
  const entities = (job.entities ?? []).filter(Boolean);
  const cats = job.inboxCategories ?? [];

  // Strongest: name the user's actual entities — "I can see Big Bitcoin Book, 2718 Capital..."
  if (entities.length >= 2) {
    return `Okay — your inbox isn't a personal account, it's an operating console. I can see ${joinList(entities.slice(0, 3))} moving through here. You're running things, not just receiving them.`;
  }
  if (entities.length === 1 && cats.length >= 1) {
    return `Okay — your inbox isn't a personal account, it's an operating console. ${entities[0]}, plus ${joinCategories(cats.slice(0, 2))}. You're running things, not just receiving them.`;
  }
  if (cats.length >= 2) {
    return `Okay — your inbox isn't a personal account, it's an operating console. ${capitalize(joinCategories(cats))}. You're running things, not just receiving them.`;
  }
  return `Okay — your inbox isn't a personal account, it's an operating console. You're running things in here, not just receiving them.`;
}

// "A, B, and C" with no trailing-category lowercasing (entities keep their case).
function joinList(items: string[]): string {
  const c = items.filter(Boolean);
  if (c.length === 0) return '';
  if (c.length === 1) return c[0];
  if (c.length === 2) return `${c[0]} and ${c[1]}`;
  return `${c.slice(0, -1).join(', ')}, and ${c[c.length - 1]}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Map internal topic keys to natural verb phrases for the "parallel efforts" line.
const TOPIC_PHRASE: Record<string, string> = {
  'bitcoin/crypto':  'Bitcoin',
  'book/publishing': 'writing a book',
  'fundraising/VC':  'running a raise',
  'legal/tax':       'keeping legal and tax close',
  'sales/deals':     'working deals',
  'hiring':          'hiring',
  'travel':          'traveling',
  'product/eng':     'shipping product',
};
function topicPhrase(t: string): string { return TOPIC_PHRASE[t] ?? t; }

// [sent] — name the bet (rank-1 topic) and the two parallel hard things (ranks 2-3).
function buildSentMessage(job: DiscoveryJobStatus): string {
  const topics = job.sentTopics ?? [];
  const r1 = topics[0], r2 = topics[1], r3 = topics[2];

  if (r1) {
    const spine = topicPhrase(r1.topic);
    // parallel efforts: ranks 2-3 both meaningful + within a close band
    const parallel = r2 && r3 && r2.count >= 8 && r3.count >= 8 && r3.count >= r2.count * 0.6;
    if (parallel) {
      return `And there it is in what you actually send: ${spine} is the spine of everything. Around it you're doing two hard things at once — ${topicPhrase(r2.topic)} and ${topicPhrase(r3.topic)}, the same stretch by the looks of it. That's a founder mid-build, not mid-idea.`;
    }
    if (r2 && r2.count >= 6) {
      return `And there it is in what you actually send: ${spine} is the spine of everything, with ${topicPhrase(r2.topic)} running right alongside it. That's a founder mid-build, not mid-idea.`;
    }
    return `And there it is in what you actually send: ${spine} is the throughline. That's a founder mid-build, not mid-idea.`;
  }
  // weak-signal fallback
  const count = job.contactCount ?? 0;
  if (count > 0) return `And there's a clear shape to what you send — a couple of efforts running in parallel. That's a founder mid-build, not mid-idea.`;
  return `I'll get to know your work as you start using Xavior.`;
}

// Round down to nearest 100 for "north of N" framing (539 -> 500). Never exact.
function roundedDown(n: number): number {
  if (n < 100) return Math.floor(n / 10) * 10;
  return Math.floor(n / 100) * 100;
}
function quoteLabels(labels: string[]): string {
  const l = labels.slice(0, 2);
  if (l.length === 0) return '';
  if (l.length === 1) return `'${l[0]}'`;
  return `'${l[0]}' and '${l[1]}'`;
}

// [calendar] — name actual projects, then turn volume into intent.
function buildCalendarMessage(job: DiscoveryJobStatus): string | null {
  if (job.status === 'failed') return null;
  if (job.isEmpty) return null;
  const past     = job.pastCount ?? 0;
  const labels   = job.protectedTimeLabels ?? [];
  const projects = (job.projects ?? []).filter(Boolean);

  if (job.isSparse) {
    return `Not much on the calendar yet — I can help you track meetings as they come in.`;
  }

  // Strongest: name the actual recurring work ("your weeks run on Libre and Atlas").
  if (projects.length >= 2) {
    const projStr = joinList(projects.slice(0, 2));
    const tail = labels.length > 0
      ? ` And you still guard ${quoteLabels(labels)} — you're defending the work, not just reacting.`
      : ` You're defending the work, not just reacting.`;
    return `Your weeks run on standing rhythms — ${projStr} recur again and again across the last 90 days.${tail}`;
  }
  if (projects.length === 1) {
    const tail = labels.length > 0 ? ` and still guard ${quoteLabels(labels)}` : '';
    return `Your calendar keeps circling back to ${projects[0]}${tail}. That's where your weeks actually go — you're defending the work, not just reacting.`;
  }

  if (past >= 100) {
    const vol = `north of ${roundedDown(past)} events behind you in the last 90 days`;
    if (labels.length > 0) {
      return `Your calendar agrees — ${vol}, and you still carve out ${quoteLabels(labels)}. You're not busy by accident. You're defending the work.`;
    }
    return `Your calendar agrees — ${vol}, and you still carve out time to think. You're not busy by accident. You're defending the work.`;
  }
  if (past > 20) {
    return `Your calendar's been active — a packed quarter behind you. You're defending the work, not just reacting.`;
  }
  return null;
}

// ─── Module-level shown cache (survives component remounts) ───────────────────

const shownByConversation = new Map<string, Set<string>>();
function getShown(conversationId: string): Set<string> {
  if (!shownByConversation.has(conversationId)) {
    shownByConversation.set(conversationId, new Set());
  }
  return shownByConversation.get(conversationId)!;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseOnboardingDiscoveryOptions {
  active: boolean;
  token: string | null | undefined;
  conversationId: string | undefined;
  addAssistantMessage: (text: string, conversationId?: string) => void;
  onAdvanceToCompleted: () => void;
  onShowMe: (pageId: string) => void;
  /** Called with the current ephemeral status text (lead-in + patience messages) or null when done. */
  onSetStatus?: (text: string | null) => void;
}

type Phase = 'inbox' | 'sent' | 'calendar' | 'letter';
const PHASE_ORDER: Phase[] = ['inbox', 'sent', 'calendar', 'letter'];

export function useOnboardingDiscovery({
  active,
  token,
  conversationId,
  addAssistantMessage,
  onAdvanceToCompleted,
  onShowMe,
  onSetStatus,
}: UseOnboardingDiscoveryOptions): void {
  const pollRef         = useRef<ReturnType<typeof setInterval> | null>(null);
  const hardTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advancedRef     = useRef(false);
  const phaseRef        = useRef<Phase>('inbox');
  const phaseEnteredRef = useRef<number>(0);       // wall-clock ms when we entered this phase
  const statusSentRef   = useRef(new Set<Phase>()); // phases whose status msg was already sent
  const patienceSentRef = useRef(new Set<Phase>()); // phases whose patience msg was sent
  const retriedRef      = useRef(false);
  const runningTicksRef = useRef(0);

  const addMessageRef   = useRef(addAssistantMessage);
  addMessageRef.current = addAssistantMessage;
  const onAdvanceRef    = useRef(onAdvanceToCompleted);
  onAdvanceRef.current  = onAdvanceToCompleted;
  const onShowMeRef     = useRef(onShowMe);
  onShowMeRef.current   = onShowMe;
  const convIdRef       = useRef(conversationId);
  convIdRef.current     = conversationId;
  const onSetStatusRef  = useRef(onSetStatus);
  onSetStatusRef.current = onSetStatus;

  useEffect(() => {
    if (!active || !conversationId) return;

    runningTicksRef.current = 0;

    const shown = getShown(conversationId);

    function send(key: string, text: string, after?: () => void, ephemeral?: boolean) {
      if (shown.has(key)) return;
      shown.add(key);
      if (ephemeral) {
        onSetStatusRef.current?.(text);
      } else {
        onSetStatusRef.current?.(null); // clear status when a real message arrives
        addMessageRef.current(text, convIdRef.current);
      }
      after?.();
    }

    function advance() {
      if (advancedRef.current) return;
      advancedRef.current = true;
      onSetStatusRef.current?.(null);
      onAdvanceRef.current();
    }

    function stopTimers() {
      if (pollRef.current) clearInterval(pollRef.current);
      if (hardTimerRef.current) clearTimeout(hardTimerRef.current);
    }

    async function restartIfStalled(data: DiscoveryStatus) {
      if (retriedRef.current) return;
      const jobs = Object.values(data.jobs);
      const allPending = jobs.every((j) => j.status === 'pending');
      const allStalled = jobs.every((j) => j.status === 'pending' || j.status === 'running');

      if (!allStalled) {
        runningTicksRef.current = 0;
        return;
      }

      // Always wait before restarting. Jobs set themselves to 'running' asynchronously
      // after kickoff — on the very first poll they may still appear 'pending'. Firing
      // restart immediately would double-kick the jobs.
      // - all pending (never started): wait 3 ticks (~4.5 s) as a race-condition grace period
      // - some running but stuck: wait 10 ticks (~15 s) for orphaned-process recovery
      runningTicksRef.current++;
      const stallThreshold = allPending ? 3 : 10;
      if (runningTicksRef.current < stallThreshold) return;

      // The jobs being "all pending" right after connect is the EXPECTED state
      // while the very first mail sync is still landing — it is not a stall. If
      // we restart-discovery now, onboarding scans an empty store, profiles an
      // empty mailbox (0 contacts, empty reveal), and freezes. So gate the
      // restart on sync readiness (mail data actually present) — restart only
      // recovers a genuinely-stuck run AFTER data has landed, never preempts the
      // first sync. Until ready, keep polling (do NOT consume the one-shot).
      const ready = await isSyncReady();
      if (!ready) {
        runningTicksRef.current = stallThreshold - 1; // re-check next tick
        return;
      }

      retriedRef.current = true;
      try {
        const wsId = getActiveWorkspaceId();
        const path = wsId
          ? `/workspaces/${wsId}/onboarding/restart-discovery`
          : '/onboarding/restart-discovery';
        await apiFetch(path, { method: 'POST', authToken: token ?? undefined });
      } catch { /* silent */ }
    }

    /** True once the initial mail sync has landed data in the store (the same
     *  `isReady = emailMessages > 0` signal GET /onboarding/sync-status uses). */
    async function isSyncReady(): Promise<boolean> {
      try {
        const wsId = getActiveWorkspaceId();
        const path = wsId
          ? `/workspaces/${wsId}/onboarding/sync-status`
          : '/onboarding/sync-status';
        const s = await apiFetch<{ isReady?: boolean }>(path, { authToken: token ?? undefined });
        return s?.isReady === true;
      } catch {
        return false; // unknown → assume not ready (don't preempt the first sync)
      }
    }

    function tick(data: DiscoveryStatus) {
      const now   = Date.now();
      const phase = phaseRef.current;
      const job   = data.jobs[phase];

      // ── 1. Enter phase: send status (lead-in) message ──────────────────────
      if (!statusSentRef.current.has(phase)) {
        statusSentRef.current.add(phase);
        phaseEnteredRef.current = now;
        send(`status-${phase}`, PHASE_STATUS[phase], undefined, true);
        return; // wait for next poll before checking result
      }

      const elapsed = now - phaseEnteredRef.current;

      // ── 2. If still waiting for MIN_PHASE_MS, optionally add patience msg ──
      if (elapsed < MIN_PHASE_MS) {
        // If job is already done but min time hasn't elapsed — show patience msg
        if (job.status === 'done' || job.status === 'failed' || job.status === 'deferred') {
          if (!patienceSentRef.current.has(phase) && elapsed >= MIN_PHASE_MS * 0.5) {
            patienceSentRef.current.add(phase);
            send(`patience-${phase}`, PHASE_PATIENCE[phase], undefined, true);
          }
        }
        return; // keep waiting
      }

      // ── 3. MIN_PHASE_MS elapsed — show result if job is done ───────────────
      const isDone = job.status === 'done' || job.status === 'failed' || job.status === 'deferred';
      if (!isDone) {
        // Job is still running past min time — show patience msg if not yet shown
        if (!patienceSentRef.current.has(phase)) {
          patienceSentRef.current.add(phase);
          send(`patience-${phase}`, PHASE_PATIENCE[phase], undefined, true);
        }
        return; // keep polling
      }

      // ── 4. Job done + min time elapsed → show result + advance phase ───────
      const currentPhaseIdx = PHASE_ORDER.indexOf(phase);
      const nextPhase = PHASE_ORDER[currentPhaseIdx + 1] as Phase | undefined;

      if (phase === 'inbox') {
        send('inbox', buildInboxMessage(job));
      } else if (phase === 'sent') {
        send('sent', buildSentMessage(job));
      } else if (phase === 'calendar') {
        const msg = buildCalendarMessage(job);
        if (msg) send('calendar', msg);
      } else if (phase === 'letter') {
        // The reveal: the LLM synthesis built from scan signal (no DB dependency).
        if (job.status === 'done' && job.revealText) {
          send('letter', job.revealText, () => {
            // Tangible follow-up so the flow doesn't dead-end on the reveal's question:
            // surface the contacts we just created, then complete.
            const n = job.contactsCount ?? 0;
            if (n > 0) {
              const who = job.contactsSummary ? ` — ${job.contactsSummary}` : '';
              setTimeout(() => {
                send('contacts', `One thing I already did: started your Contacts with the ${n} people you email most${who}. Anyone else you correspond with is waiting in Contacts → Potential — confirm them there to add. Say the word and I'll enrich the ones I added — roles, companies, the works.`);
                advance();
              }, INTER_PHASE_MS);
            } else {
              advance();
            }
          });
        } else if (job.status === 'done' && job.pageId) {
          // legacy page path (kept for back-compat if a page was generated)
          send('letter', `Here's what I picked up — opening it now.`, () => {
            onShowMeRef.current(job.pageId!);
            advance();
          });
        } else {
          // deferred/failed — distinguish a genuinely empty account from a scan failure
          const inboxJob = data.jobs.inbox;
          const sentJob  = data.jobs.sent;
          const isEmptyAcc = !inboxJob.fetchFailed && inboxJob.status !== 'failed'
            && (inboxJob.emailCount ?? 0) < 10 && (sentJob.contactCount ?? 0) <= 1;
          send(
            'empty-account',
            isEmptyAcc
              ? `Looks like a fresh inbox — so you're just kicking the tires. Fair enough. Got anything you want to throw at me to work on together?`
              : `Still piecing your full picture together — I'll have more the moment my sync catches up.`,
            () => advance(),
          );
        }
        stopTimers();
        return;
      }

      // Advance to next phase after a short inter-phase pause
      if (nextPhase) {
        phaseRef.current        = nextPhase;
        phaseEnteredRef.current = now + INTER_PHASE_MS; // give brief pause before next status
      } else {
        // Ran out of phases without hitting the letter step
        stopTimers();
        advance();
      }
    }

    const poll = async () => {
      try {
        const wsId = getActiveWorkspaceId();
        const path = wsId
          ? `/workspaces/${wsId}/onboarding/discovery-status`
          : '/onboarding/discovery-status';
        const data = await apiFetch<DiscoveryStatus>(path, { authToken: token ?? undefined });
        void restartIfStalled(data);
        tick(data);
      } catch { /* keep polling */ }
    };

    void poll();
    pollRef.current = setInterval(() => { void poll(); }, POLL_MS);

    hardTimerRef.current = setTimeout(() => {
      stopTimers();
      send('timeout', `Taking a bit longer than usual — feel free to look around. I'll let you know when it's ready.`);
      advance();
    }, HARD_TIMEOUT_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (hardTimerRef.current) clearTimeout(hardTimerRef.current);
    };
  }, [active, conversationId, token]);
}
